// src/store/thunks/create-six/createSixThunk.ts
import type { SixCreated } from "@/services/abstract/CreateSixService";
import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { isAPIError } from "@/store/utils";
import type { CreateSixPayload, SixSummary } from "@/types/six/Six";
import { withErrorHandling, withLoader } from "@/store/thunks/thunkWrappers";
import { HTTP_STATUS_CONFLICT } from "@/config/apiConfig";
import { mapSixesResponse } from "@/store/mappers/mapSixesResponse";
import { fetchSixCatalog } from "@/store/thunks/sixCatalog/fetchSixCatalog";

const POLL_INTERVAL_MS = 5000; // 5 seconds
const MAX_POLL_DURATION_MS = 60000; // 60 seconds
const MAX_ATTEMPTS = MAX_POLL_DURATION_MS / POLL_INTERVAL_MS; // 12 attempts

export const createSixThunk = createAppAsyncThunk<
  SixSummary | null,
  CreateSixPayload,
  { rejectValue: { fieldErrors: Record<string, string> } }
>(
  "createSix/createSixThunk",
  withLoader(
    withErrorHandling(
      async(payload, thunkAPI) => {
        const { extra, rejectWithValue, dispatch } = thunkAPI;
        const log = extra.loggerService.createLogger({ namespace: 'six3ro:six:create' });

        // Step 1: Create the Six
        let sixId: string;
        try {
          const result = await extra.createSixService.createSix(payload);
          // API returns sixId in the response, handle both id and sixId for compatibility
          sixId = result.id ?? (result as unknown as { sixId: string }).sixId;
        } catch (error) {
          if (isAPIError(error) && error.status === HTTP_STATUS_CONFLICT) {
            return rejectWithValue({ fieldErrors: { name: "Duplicate name" } });
          }
          throw error;
        }

        if (!sixId) {
          log.warn('createSixThunk: No sixId returned from API');
          void dispatch(fetchSixCatalog());
          return null;
        }

        // Step 2: Poll until bootstrapNodes are available
        log.info(`Polling for bootstrapNodes for Six ${sixId}`);

        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          const apiResponse = await extra.sixCatalogService.fetchSixes();
          const sixes = mapSixesResponse(apiResponse);

          const targetSix = sixes.find(six => six.id === sixId);

          if (targetSix?.bootstrapNodes !== undefined && targetSix.bootstrapNodes.length > 0) {
            log.info(`Six ${sixId} has bootstrapNodes after ${attempt + 1} attempts`);
            // Update Redux state and initialize P2P stack
            void dispatch(fetchSixCatalog());
            return targetSix;
          }

          if (attempt < MAX_ATTEMPTS - 1) {
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
          }
        }

        log.warn(`Polling timeout: Six ${sixId} has no bootstrapNodes after ${MAX_ATTEMPTS} attempts`);
        // Still refresh the catalog even on timeout so the Six appears in the list
        void dispatch(fetchSixCatalog());
        return null;
      }
    )
  )
);
